import {useEffect, useState} from 'react';
import dayjs from 'dayjs';

import http from 'services/http';
import formatarMoeda from 'utils/formatMoney';
import styles from './styles.module.css';
import TabelaDescargaEnviada from 'components/Financeiro/TabelaDescargaEnviada';

import jsPDF from 'jspdf';
import 'jspdf-autotable';

export default function CaixaResumo() {
    const [banca, setBanca] = useState('');
    const [geradoEm, setGeradoEm] = useState('');
    const [resumo, setResumo] = useState(null);
    const [relatorio, setRelatorio] = useState([]);
    const [relatorioDescarga, setRelatorioDescarga] = useState([]);
    const [resumoDescarga, setResumoDescarga] = useState(null);
    const [lancamentos, setLancamentos] = useState([]);
    const [loading, setLoading] = useState(true);

    const [supervisores, setSupervisores] = useState([]);
    const [gerentes, setGerentes] = useState([]);
    const [cambistas, setCambistas] = useState([]);
    const [loterias, setLoterias] = useState([]);
    const [supervisorId, setSupervisorId] = useState('');
    const [gerenteId, setGerenteId] = useState('');
    const [cambistaId, setCambistaId] = useState('');
    const [loteriaId, setLoteriaId] = useState('');
    const [categoria, setCategoria] = useState('');

    const nivel = localStorage.getItem('nivel');
    const modulos = JSON.parse(localStorage.getItem('usuario'))?.modulosAtivos ?? [];

    async function buscarResumo(filtros = {}) {
        try {
            setLoading(true);

            // Relatório geral do caixa em aberto — sem recorte de data por definição.
            const response = await http.get('/caixa/analitico', {
                params: {
                    supervisor_id: filtros.supervisorId || undefined,
                    gerente_id: filtros.gerenteId || undefined,
                    cambista_id: filtros.cambistaId || undefined,
                    loteria_id: filtros.loteriaId || undefined,
                    categoria: filtros.categoria || undefined,
                }
            });
            const {banca, gerado_em, relatorio, resumo, relatorio_descarga, resumo_descarga} = response.data.data;

            const lancamentos = response.data.data.lancamentos.map((lancamento) => {
                lancamento.data = dayjs(lancamento.created_at).format(
                    'DD/MM/YYYY HH:mm'
                );
                lancamento.valor = formatarMoeda(lancamento.valor);
                return lancamento;
            });

            setRelatorio(relatorio);
            setResumo(resumo);
            setRelatorioDescarga(relatorio_descarga || []);
            setResumoDescarga(resumo_descarga || null);
            setLancamentos(lancamentos);
            setBanca(banca);
            setGeradoEm(gerado_em);
        } catch (e) {
            console.log(e)
        }

        setLoading(false);
    }

    // Sem escopo traz todos os cambistas; com gerente ou grupo, só os de dentro dele.
    async function buscarCambistas(escopo = {}) {
        try {
            const response = await http.get('/usuarios', {
                params: {
                    nivel: 'operador',
                    resumida: true,
                    gerente_id: escopo.gerenteId || undefined,
                    supervisor_id: escopo.supervisorId || undefined,
                }
            });
            setCambistas(response.data.data ?? []);
        } catch (e) {
            console.log(e)
        }
    }

    useEffect(() => {
        buscarResumo();
        buscarCambistas();

        http.get('/loterias', {params: {todas: 1}})
            .then((r) => setLoterias(r.data.data ?? []))
            .catch(() => {});

        if (nivel === 'admin') {
            http.get('/usuarios', {params: {nivel: 'gerente', resumida: true}})
                .then((r) => setGerentes(r.data.data ?? []))
                .catch(() => {});

            http.get('/supervisores', {params: {resumida: true}})
                .then((r) => setSupervisores(r.data.data ?? []))
                .catch(() => {});
        }
    }, []);

    // Grupo e gerente são excludentes; trocar qualquer um dos dois zera o cambista.
    function mudarSupervisor(valor) {
        setSupervisorId(valor);
        setGerenteId('');
        setCambistaId('');
        buscarCambistas({supervisorId: valor});
    }

    function mudarGerente(valor) {
        setGerenteId(valor);
        setSupervisorId('');
        setCambistaId('');
        buscarCambistas({gerenteId: valor});
    }

    function aplicarFiltros(e) {
        e.preventDefault();
        buscarResumo({supervisorId, gerenteId, cambistaId, loteriaId, categoria});
    }

    async function downloadPdf() {
        const doc = new jsPDF('l', 'pt', 'a4');
        doc.setFontSize(24);
        doc.text(`Fechamento de caixa: ${banca} - ${geradoEm}`, 40, 30);

        doc.autoTable({
            html: `.tablePDF`,
            useCss: true
        });

        doc.autoTable({
            html: `.table_geral`,
            useCss: true
        });

        doc.autoTable({
            html: `.table_title`,
            useCss: true
        });

        doc.autoTable({
            html: `.table_box`,
            useCss: true
        });

        doc.setFontSize(5);
        doc.save(`Resumo_Fechamento_${geradoEm}.pdf`);
    }

    // Sem rótulos: o nome do filtro é a própria opção vazia do select. Linha única
    // ocupando toda a largura, para acompanhar as tabelas logo abaixo.
    const filtros = (
        <form onSubmit={aplicarFiltros} className="m-b">
            <div className="d-flex cinco-colunas justify-content-start">
                {nivel === 'admin' && (
                    <>
                        <select
                            className="form-control"
                            value={supervisorId}
                            onChange={(e) => mudarSupervisor(e.target.value)}
                        >
                            <option value="">Grupo Gerentes</option>
                            {supervisores.map((s) => (
                                <option key={s.id} value={s.id}>{s.nome}</option>
                            ))}
                        </select>

                        <select
                            className="form-control"
                            value={gerenteId}
                            onChange={(e) => mudarGerente(e.target.value)}
                        >
                            <option value="">Gerente</option>
                            {gerentes.map((g) => (
                                <option key={g.id} value={g.id}>{g.login.toUpperCase()}</option>
                            ))}
                        </select>
                    </>
                )}

                <select
                    className="form-control"
                    value={cambistaId}
                    onChange={(e) => setCambistaId(e.target.value)}
                >
                    <option value="">Cambista</option>
                    {cambistas.map((c) => (
                        <option key={c.id} value={c.id}>{c.login.toUpperCase()}</option>
                    ))}
                </select>

                <select
                    className="form-control"
                    value={categoria}
                    onChange={(e) => { setCategoria(e.target.value); setLoteriaId(''); }}
                >
                    <option value="">Categoria</option>
                    {modulos.includes('JB') && <option value="jb">Tradicional</option>}
                    {modulos.includes('Raspadinha') && <option value="raspadinha">Raspadinha</option>}
                </select>

                {(!categoria || categoria === 'jb') && (
                    <select
                        className="form-control"
                        value={loteriaId}
                        onChange={(e) => setLoteriaId(e.target.value)}
                    >
                        <option value="">Extração</option>
                        {loterias.map((l) => (
                            <option key={l.id} value={l.id}>{l.nome}</option>
                        ))}
                    </select>
                )}

                <button type="submit" className="btn btn-primary" style={{height: '38px'}}>
                    Buscar
                </button>
            </div>
        </form>
    );

    return loading ? (
        <>
            {filtros}
            <div className="loader"/>
        </>
    ) : (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                <h2>
                    <b>Fechamento de caixa: {banca}</b>
                </h2>
            </div>
            <br/>
            <h4>
                <b>{geradoEm}</b>
            </h4>

            <br/>

            {filtros}

            <br/>

            <section className={styles.containerTablesGerente}>
                <div className={styles.borderColor}>
                    <table className="table tablePDF">
                        {relatorio?.map((gerente) => (
                            <>
                                <tr
                                    key={`gerente-${gerente.nome}`}
                                    style={{backgroundColor: '#7F7F7F', color: '#fff'}}
                                    className={styles.containerTablesGerenteHeadPrimary}
                                >
                                    <th colSpan="9">
                                        <span>{gerente.nome.toUpperCase()}</span>
                                    </th>
                                </tr>
                                <tr
                                    className={styles.containerTablesGerenteHead}
                                    style={{backgroundColor: '#ccc'}}
                                >
                                    <th>Operador</th>
                                    <th>Qtd.</th>
                                    <th>Entradas</th>
                                    <th>Saída</th>
                                    <th>Comissões</th>
                                    <th>Lançamentos</th>
                                    <th>Saldo Cambista</th>
                                    <th>Saldo Banca</th>
                                </tr>
                                <tbody className={styles.containerTablesGerenteBody}>
                                {gerente?.cambistas?.map((cambista) => {
                                    return (
                                        <tr key={`operador-${cambista.cambista_id}`}>
                                            <td className={styles.fontBold}>{cambista.cambista.toUpperCase()}</td>
                                            <td className={styles.fontBold}>{cambista.apostas}</td>
                                            <td>{formatarMoeda(cambista.entrada)}</td>
                                            <td className="text-black">
                                                {formatarMoeda(cambista.saida)}
                                            </td>
                                            <td className="text-black">
                                                {formatarMoeda(cambista.comissao_cambista)}
                                            </td>
                                            <td>
												<span
                                                    className={`${
                                                        cambista.lancamento < 0
                                                            ? 'text-danger'
                                                            : cambista.lancamento > 0
                                                                ? 'text-success'
                                                                : 'text-black'
                                                    }`}
                                                >
													{formatarMoeda(cambista.lancamento)}
												</span>
                                            </td>
                                            <td className={styles.fontBold}>
												<span
                                                    className={`${
                                                        cambista.total_cambista < 0
                                                            ? 'text-danger'
                                                            : cambista.total_cambista > 0
                                                                ? 'text-success'
                                                                : 'text-black'
                                                    }`}
                                                >
													{formatarMoeda(cambista.total_cambista)}
												</span>
                                            </td>
                                            <td className={styles.fontBold}>
												<span
                                                    className={`${
                                                        cambista.total_banca < 0
                                                            ? 'text-danger'
                                                            : cambista.total_banca > 0
                                                                ? 'text-success'
                                                                : 'text-black'
                                                    }`}
                                                >
													{formatarMoeda(cambista.total_banca)}
												</span>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                                <tr className={styles.summaryTableGerente}>
                                    <th>TOTAL</th>
                                    <th>{gerente.apostas}</th>
                                    <th>{formatarMoeda(gerente.entrada)}</th>
                                    <th className="text-black">{formatarMoeda(gerente.saida)}</th>

                                    <th className="text-black">
                                        {formatarMoeda(gerente.comissao_cambista)}
                                    </th>
                                    <th
                                        className={`${
                                            gerente.lancamento < 0 ? 'text-danger' : (gerente.lancamento > 0) ? 'text-success' : 'text-black'
                                        }`}
                                    >
                                        {formatarMoeda(gerente.lancamento)}
                                    </th>
                                    <th
                                        className={`${
                                            gerente.total_cambista < 0 ? 'text-danger' : (gerente.total_cambista > 0) ? 'text-success' : 'text-black'
                                        }`}
                                    >
                                        {formatarMoeda(gerente.total_cambista)}
                                    </th>
                                    <th
                                        className={`${
                                            gerente.total_banca < 0 ? 'text-danger' : (gerente.total_banca > 0) ? 'text-success' : 'text-black'
                                        }`}
                                    >
                                        {formatarMoeda(
                                            gerente.total_banca
                                        )}
                                    </th>
                                </tr>
                                <tr className={styles.footerTableGerente}>
                                    <th colspan='5'>
                                        {gerente.nome.toUpperCase()}:{' '}
                                        <span
                                            className={
                                                gerente.comissao_gerente < 0
                                                    ? 'text-danger'
                                                    : gerente.comissao_gerente > 0
                                                        ? 'text-success'
                                                        : 'text-black'
                                            }
                                        >
													{formatarMoeda(gerente.comissao_gerente)}
												</span>
                                    </th>
                                    <th colspan='4'>
                                        BANCA:{' '}
                                        <span
                                            className={
                                                (gerente.total_banca - gerente.comissao_gerente) < 0
                                                    ? 'text-danger'
                                                    : gerente.total_banca > 0
                                                        ? 'text-success'
                                                        : ''
                                            }
                                        >
													{formatarMoeda(gerente.total_banca - gerente.comissao_gerente)}
												</span>
                                    </th>
                                </tr>
                                <tr>
                                    <td className={styles.borderBetWeenTable} colspan="9"></td>
                                </tr>
                            </>
                        ))}
                    </table>
                </div>

                {/* RESUMO GERAL */}
                <div className="borderTable m-t">
                    <table className={'table table-hover table_geral' + ' ' + styles.summaryBox}>
                        <tr
                            style={{
                                color: '#fff',
                                backgroundColor: '#008800',
                            }}
                        >
                            <th colSpan="9">TOTAL GERAL DA BANCA</th>
                        </tr>

                        <tr style={{color: '#fff', backgroundColor: '#008800'}}>
                            <th/>
                            <th>Qtd.</th>
                            <th>Entradas</th>
                            <th>Saída</th>
                            <th>Comissões</th>
                            <th>Lançamentos</th>
                            <th>Saldo Cambista</th>
                            <th>Saldo Banca</th>
                        </tr>

                        {resumo && (
                            <tbody>
                            <tr>
                                <th>TOTAL</th>
                                <th>{resumo.apostas}</th>
                                <th>{formatarMoeda(resumo.entrada)}</th>
                                <th className="text-black">{formatarMoeda(resumo.saidas)}</th>
                                <th className="text-black">
                                    {formatarMoeda(resumo.comissoes)}
                                </th>
                                <th>{formatarMoeda(resumo.lancamento)}</th>
                                <th>
										<span
                                            className={`${
                                                resumo.total_cambista < 0
                                                    ? 'text-danger'
                                                    : resumo.total_cambista > 0
                                                        ? 'text-success'
                                                        : 'text-black'
                                            }`}
                                        >
											{formatarMoeda(resumo.total_cambista)}
										</span>
                                </th>
                                <th>
										<span
                                            className={`${
                                                resumo.total_banca < 0
                                                    ? 'text-danger'
                                                    : resumo.total_banca > 0
                                                        ? 'text-success'
                                                        : 'text-black'
                                            }`}
                                        >
											{formatarMoeda(resumo.total_banca)}
										</span>
                                </th>
                            </tr>
                            </tbody>
                        )}
                    </table>
                </div>

                {/* DESCARGA ENVIADA */}
                <TabelaDescargaEnviada
                    relatorio={relatorioDescarga}
                    resumo={resumoDescarga}
                    formatMoney={formatarMoeda}
                />

                <br/>
                <br/>
                <div style={{ fontSize: '32px', marginBottom: '10px' }}>Lançamentos de Caixa:</div>
                <div className={`borderTable m-t ${styles.containerTablesLancamento}`}>
                    <table
                        className="table table-hover table_box"
                        style={{backgroundColor: '#ccc', fontWeight: 'bold'}}
                    >
                        <tr className={styles.containerTablesLancamentoHead}>
                            <th>Operador</th>
                            <th>Valor</th>
                            <th>Tipo</th>
                            <th>Data</th>
                        </tr>

                        <tbody>
                        {lancamentos.map((lancamento) => (
                            <tr key={lancamento.id}>
                                <td>{lancamento.nome.toUpperCase()}</td>
                                <td>
                                    {lancamento.tipo === 'credito' ? (
                                        <span className="text-success font-weight-bold">
                                                {lancamento.valor}
                                            </span>
                                    ) : (
                                        <span className="text-danger font-weight-bold">
                                                - {lancamento.valor}
                                            </span>
                                    )}
                                </td>
                                <td>{lancamento.descricao}</td>
                                <td>{lancamento.data}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            </section>
            <button className={`btn btn-primary ${styles.salvarPDF}`} onClick={downloadPdf}>
                Salvar PDF
            </button>
        </>
    );
}
